import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { MembershipForm } from "./MembershipForm";
import {
  useFetchSaccoLoanMembershipRepayments,
  useDeleteSaccoMembership,
  useSaccoMetrics,
  useFetchSaccoShares,
  useFetchSaccoWithdrawals,
  useFetchSaccoDeposits
} from "@saccoHooks";
import {SaccoMembership} from "@/@types/sacco.models.ts";

interface MembershipCardProps {
  membership: SaccoMembership;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function MembershipCard({
  membership,
  displayCurrency,
  convert,
  isLoading: ratesLoading,
  error: ratesError,
}: MembershipCardProps) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: deposits = [] } = useFetchSaccoDeposits(membership.id);
  const { data: withdrawals = [] } = useFetchSaccoWithdrawals(membership.id);
  const { data: shares = [] } = useFetchSaccoShares(membership.id);
  const { data: repayments = [], isLoading: metricsLoading } =
    useFetchSaccoLoanMembershipRepayments(membership.id);

  const metrics = useSaccoMetrics({ deposits, withdrawals, shares, repayments });
  const deleteMembership = useDeleteSaccoMembership();

  function handleCardClick() {
    navigate(`/investments/sacco/${membership.id}`);
  }

  function handleEditClick(e: React.MouseEvent) {
    e.stopPropagation();
    setEditOpen(true);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    try {
      await deleteMembership.mutateAsync(membership.id);
      toast.success("Membership deleted successfully");
    } catch {
      toast.error("Failed to delete membership");
    }
  }

  const hasOutstandingLoan = metrics.outstandingLoanBalance > 0;

  const nativeCurrency = membership.currency ?? 'KES';
  const effectiveDisplayCurrency = displayCurrency ?? nativeCurrency;

  function convertAmount(amount: number): number {
    if (ratesError || !convert || effectiveDisplayCurrency === nativeCurrency) {
      return amount;
    }
    try {
      return convert(amount, nativeCurrency, effectiveDisplayCurrency);
    } catch {
      return amount;
    }
  }

  function renderAmount(amount: number) {
    if (ratesLoading) {
      return <Skeleton className="h-4 w-20 inline-block" />;
    }
    return formatCurrency(convertAmount(amount), ratesError ? nativeCurrency : effectiveDisplayCurrency);
  }

  return (
    <>
      <Card
        className="w-full cursor-pointer hover:shadow-md transition-shadow"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
        aria-label={`View details for ${membership.sacco_name}`}
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold">
              {membership.sacco_name}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {membership.membership_number}
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {metricsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Net Position:</span>
                <span
                  className={`font-medium ${
                    metrics.netPosition >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {renderAmount(metrics.netPosition)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Loan Eligibility:</span>
                <span className="font-medium">
                  {renderAmount(metrics.loanEligibility)}
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleEditClick}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-red-600 hover:text-red-700"
              onClick={handleDeleteClick}
              disabled={deleteMembership.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      <MembershipForm
        open={editOpen}
        onOpenChange={setEditOpen}
        membership={membership}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Membership</AlertDialogTitle>
            <AlertDialogDescription>
              {hasOutstandingLoan ? (
                <>
                  This membership has an outstanding loan balance of{" "}
                  <strong>{formatCurrency(metrics.outstandingLoanBalance, nativeCurrency)}</strong>.
                  Deleting it will permanently remove all associated deposits,
                  shares, loans, repayments, rebates, and goals. This action
                  cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{membership.sacco_name}</strong>? This will permanently
                  remove all associated deposits, shares, loans, repayments,
                  rebates, and goals. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
